import { MoneyError } from '#money/errors'

/**
 * The currency registry. **Declared here, not imported from Dinero's ISO list.**
 *
 * Dinero ships `IDR` at `exponent: 2`, which is what ISO 4217 says and not what Indonesia
 * does: prices are quoted, invoiced and settled in whole rupiah, and a gateway that returns
 * `150000` means one hundred fifty thousand rupiah, not fifteen hundred. Taking the ISO
 * exponent would make every IDR amount in the system a hundred times too small — and it
 * would do it consistently enough to look correct until the first reconciliation.
 *
 * So the exponent is a decision this platform makes, once, here. Adding a currency is
 * adding a line to this object, which is the point: a currency the registry does not
 * declare is refused at the codec rather than defaulting to two decimals.
 */
export const CURRENCIES = {
  /** Indonesian rupiah. **No sub-unit** — see above. The gateway rails settle only in this. */
  IDR: { code: 'IDR', base: 10, exponent: 0 },
  USD: { code: 'USD', base: 10, exponent: 2 },
  EUR: { code: 'EUR', base: 10, exponent: 2 },
  SGD: { code: 'SGD', base: 10, exponent: 2 },
  MYR: { code: 'MYR', base: 10, exponent: 2 },
  AUD: { code: 'AUD', base: 10, exponent: 2 },
  /** Japanese yen, also without a sub-unit — so the exponent-0 path is exercised by more than one. */
  JPY: { code: 'JPY', base: 10, exponent: 0 },
} as const satisfies Record<string, { code: string; base: 10; exponent: number }>

export type CurrencyCode = keyof typeof CURRENCIES
export type Currency = (typeof CURRENCIES)[CurrencyCode]

// oxlint-disable-next-line typescript/no-unsafe-type-assertion
export const CURRENCY_CODES = Object.keys(CURRENCIES) as CurrencyCode[]

export function isCurrencyCode(value: string): value is CurrencyCode {
  return value in CURRENCIES
}

/** The registry entry, or a refusal naming the code. Never a two-decimal default. */
export function currencyOf(code: string): Currency {
  if (!isCurrencyCode(code)) {
    throw new MoneyError('unknown_currency', `\`${code}\` is not a declared currency.`, {
      currency: code,
      declared: CURRENCY_CODES,
    })
  }
  return CURRENCIES[code]
}

/** How many decimal places this currency's minor unit occupies. */
export function exponentOf(code: CurrencyCode): number {
  return CURRENCIES[code].exponent
}
